/**
 * Parses `log_lines.json` into structured `LogEvent`s and exposes a
 * since-seq cursor over them (requirement 4: "a scrubber keyed on event
 * seq that drives the brain playback and all panels to that seq").
 *
 * `log_lines.json` today only ever holds the *tail* of the catalog (the
 * last `max_log_lines`, default 500 — `organism.observatory.publish
 * .DEFAULT_MAX_LOG_LINES`), not a chunked full history — there is no
 * paged "since seq" file on disk yet. `EventStreamCursor` still gives the
 * log panel and the scrubber a real since-seq view *within that tail
 * window*: each poll re-parses the (possibly grown) tail and
 * `sinceSeq(seq)` filters to what's new, which is exactly what "follow
 * live" mode needs. Scrubbing to a seq outside the current tail window is a
 * real, styled state ("older than the published window"), not a crash.
 */

import type { LogEvent } from "../types/events";
import type { IncrementalEvent } from "../types/incremental";

const LINE_PATTERN = /^(\S+) seq=(\d+) kind=(\S+) organism=(\S+) event_time=(\S+)$/;

/** Parses one `log_lines.json` line. Malformed lines still produce a
 * `LogEvent` — with `seq = -1` and every parsed field `null` — carrying the
 * raw text, so a future publisher format change degrades to "unparsed",
 * never a dropped line or a crash. */
export function parseLogLine(line: string): LogEvent {
  const match = LINE_PATTERN.exec(line);
  if (!match) {
    return {
      seq: -1,
      kind: "unknown",
      organismId: null,
      recordedTime: "",
      eventTime: "",
      knowledgeTime: null,
      digest: null,
      raw: line,
    };
  }
  const [, recordedTime, seqText, kind, organism, eventTime] = match;
  return {
    seq: Number.parseInt(seqText ?? "-1", 10),
    kind: kind ?? "unknown",
    organismId: organism === "-" ? null : (organism ?? null),
    recordedTime: recordedTime ?? "",
    eventTime: eventTime ?? "",
    knowledgeTime: null,
    digest: null,
    raw: line,
  };
}

export function parseLogLines(lines: readonly string[]): readonly LogEvent[] {
  return lines.map(parseLogLine);
}

/** A since-seq view over one snapshot poll's log lines. Immutable — a new
 * poll builds a new cursor rather than mutating in place, which is what
 * makes "scrubbing to a seq twice yields identical panel state" trivially
 * true (the derived view is a pure function of the events array + seq). */
export class EventStreamCursor {
  constructor(private readonly events: readonly LogEvent[]) {}

  get all(): readonly LogEvent[] {
    return this.events;
  }

  get maxSeq(): number {
    let max = -1;
    for (const event of this.events) {
      if (event.seq > max) max = event.seq;
    }
    return max;
  }

  get minSeq(): number {
    let min = Number.POSITIVE_INFINITY;
    for (const event of this.events) {
      if (event.seq >= 0 && event.seq < min) min = event.seq;
    }
    return Number.isFinite(min) ? min : -1;
  }

  /** Events strictly after `seq` (for "follow live" / incremental polling). */
  sinceSeq(seq: number): readonly LogEvent[] {
    return this.events.filter((event) => event.seq > seq);
  }

  /** Events at or before `seq` — the deterministic state the scrubber
   * reproduces for a given seq, independent of poll history. */
  upToSeq(seq: number): readonly LogEvent[] {
    return this.events.filter((event) => event.seq >= 0 && event.seq <= seq);
  }

  /** Whether `seq` falls inside the currently-published tail window. */
  hasSeq(seq: number): boolean {
    return seq >= this.minSeq && seq <= this.maxSeq;
  }

  filterByKind(predicate: (kind: string) => boolean): EventStreamCursor {
    return new EventStreamCursor(this.events.filter((event) => predicate(event.kind)));
  }

  filterByOrganism(organismId: string): EventStreamCursor {
    return new EventStreamCursor(
      this.events.filter((event) => event.organismId === organismId || event.organismId === null),
    );
  }
}

/** Converts one `IncrementalEvent` (v2, `events/<from>-<to>.json`) into the
 * common `LogEvent` shape, carrying its full payload along (unlike a v1
 * parsed log line, which never had one). */
export function incrementalEventToLogEvent(event: IncrementalEvent): LogEvent {
  return {
    seq: event.seq,
    kind: event.kind,
    organismId: event.organism_id,
    recordedTime: event.recorded_time,
    eventTime: event.event_time,
    knowledgeTime: event.knowledge_time,
    digest: event.payload_digest,
    payload: event.payload,
    raw: JSON.stringify(event),
  };
}
